/**
 * Professional experience — mirrors Fantahun_Fekadu_Resume_Tech_Lead.pdf.
 */
export const experiences = [
  {
    id: 'alphamail',
    company: 'AlphaMail AI',
    short: 'AlphaMail AI',
    role: 'Senior AI/ML Engineer',
    location: 'San Francisco, California, USA',
    period: 'Jan 2026 — Present',
    type: 'Full-time',
    current: true,
    summary:
      'Design and ship production-grade AI applications — LLM orchestration, retrieval and conversational workflows wired into real enterprise systems.',
    stack: ['Python', 'FastAPI', 'LLM APIs', 'RAG', 'Vector Search', 'Observability'],
    points: [
      'Design and develop production-grade AI-powered applications using Python and FastAPI, integrating LLMs, knowledge bases, APIs and enterprise systems.',
      'Design solutions for LLM orchestration, retrieval-augmented generation (RAG), conversational workflows, context management and business-specific knowledge.',
      'Build backend services and APIs that connect AI capabilities with frontend applications, CRM platforms, knowledge systems and external business services.',
      'Develop context-aware conversational systems supporting natural-language understanding, multi-turn interaction, intent recognition and automated business workflows.',
      'Implement reliable AI pipelines with structured generation, validation, failure handling, retries, provider abstraction and production observability.',
      'Work across multiple LLM providers and design provider-agnostic orchestration layers to improve reliability, maintainability and flexibility.',
      'Investigate and resolve complex AI-system failures spanning model responses, context handling, document generation, retrieval and API integrations.',
      'Apply automated testing and AI evaluation practices to validate complex workflows and prevent regressions.',
    ],
  },
  {
    id: 'santimpay',
    company: 'SantimPay Financial Solutions',
    short: 'SantimPay',
    role: 'Senior Software Developer / Technical Lead',
    location: 'Addis Ababa, Ethiopia',
    period: 'Aug 2022 — Present',
    type: 'Full-time',
    current: true,
    summary:
      'Lead the architecture and delivery of mission-critical payment infrastructure — merchant services, transaction processing and bank integrations.',
    stack: ['Go', 'Python/FastAPI', 'Node.js', 'PostgreSQL', 'Docker', 'OAuth2 / JWT'],
    points: [
      'Lead the architecture, development and delivery of mission-critical financial and payment systems supporting merchant services, transaction processing and enterprise integrations.',
      'Design and develop scalable backend services using Go, Python/FastAPI, Node.js and PostgreSQL.',
      'Define service architectures, API contracts, data models, integration boundaries and reliability strategies for production systems.',
      'Lead development of distributed backend workflows integrating banks, payment gateways, SMS providers, email services and third-party enterprise platforms.',
      'Lead and mentor frontend and backend engineers while staying hands-on with architecture, implementation, debugging and production support.',
      'Make technical decisions around scalability, performance, security, data consistency, service boundaries and maintainability.',
      'Design and implement secure APIs using JWT, OAuth2, RBAC and modern API security practices.',
      'Lead root-cause analysis and resolution of complex production incidents across backend, database, infrastructure and integration layers.',
      'Drive Docker-based development environments, CI/CD improvements, deployment planning, monitoring and production reliability.',
    ],
  },
  {
    id: 'evinfinity',
    company: 'EV Infinity',
    short: 'EV Infinity',
    role: 'Senior Backend Developer',
    location: 'London, UK · Remote',
    period: 'Sep 2025 — May 2026',
    type: 'Contract',
    summary:
      'Architected backend services for a multi-network EV charging platform, including roaming interoperability between independent charge point operators.',
    stack: ['C#', 'ASP.NET Core', 'Entity Framework Core', 'Hubject', 'SQL Server', 'JWT / OAuth2'],
    points: [
      'Architected and built scalable production backend services using C#, ASP.NET Core and Entity Framework Core for a multi-network EV charging platform.',
      'Designed APIs for charger discovery, charging sessions, payment processing, user management and external platform integrations.',
      'Made key architecture decisions around service boundaries, API contracts, interoperability, authentication, data consistency and integration reliability.',
      'Led the technical integration of EV charging network roaming protocols, including Hubject, enabling interoperability across independently managed charge point operators.',
      'Integrated third-party payment gateways and external charging-network services.',
      'Implemented JWT/OAuth2 authentication and role-based access control.',
      'Optimised backend services and database interactions to support reliable production workloads.',
    ],
  },
  {
    id: 'ivng',
    company: 'IVNG Media LLC',
    short: 'IVNG Media',
    role: 'Senior Backend Developer',
    location: 'New York, USA · Remote',
    period: 'Jan 2025 — Apr 2026',
    type: 'Contract',
    summary:
      'Built and maintained production web and mobile backends, with hands-on frontend work where features crossed the stack.',
    stack: ['Go', 'Laravel', 'React', 'React Native', 'REST APIs'],
    points: [
      'Built and maintained production applications using Go, Laravel, React and React Native.',
      'Developed scalable backend services and REST APIs supporting web and mobile applications.',
      'Contributed to frontend development using React and integrated user-facing features with backend APIs.',
      'Implemented authentication, authorization, API security and third-party service integrations.',
      'Improved API performance through backend optimisation and efficient database queries.',
      'Troubleshot complex issues across frontend, backend, database and integration layers.',
      'Participated in technical discussions and code reviews to improve application quality and maintainability.',
    ],
  },
  {
    id: 'salesbos',
    company: 'Salesbos Solutions',
    short: 'Salesbos',
    role: 'Full Stack Developer',
    location: 'Canada · Remote',
    period: 'Jan 2023 — Oct 2024',
    type: 'Freelance',
    summary:
      'Delivered serverless full-stack products end to end on AWS, working directly with clients from requirements to production.',
    stack: ['Go', 'JavaScript', 'AWS Lambda', 'API Gateway', 'DynamoDB', 'S3', 'SNS'],
    points: [
      'Built scalable backend services and full-stack applications using Go, JavaScript, REST APIs, AWS and DynamoDB.',
      'Built serverless backend services using AWS Lambda and API Gateway.',
      'Integrated AWS services including S3, SNS, DynamoDB and CloudWatch.',
      'Developed frontend functionality in JavaScript and integrated it with backend REST APIs.',
      'Improved application performance, scalability and reliability through backend and infrastructure optimisation.',
      'Worked independently with clients to understand requirements, design solutions and deliver production-ready software.',
    ],
  },
  {
    id: 'sidehustle',
    company: 'Side Hustle',
    short: 'Side Hustle',
    role: 'Mobile Application Developer',
    location: 'Ibadan, Nigeria',
    period: '2021 — 2022',
    type: 'Full-time',
    summary:
      'Designed and shipped cross-platform mobile apps, focused on interfaces that kept users engaged and backends they could rely on.',
    stack: ['Flutter', 'React Native', 'REST APIs', 'Firebase'],
    points: [
      'Designed and deployed cross-platform mobile apps using Flutter and React Native.',
      'Built intuitive UI/UX components to improve engagement.',
      'Integrated REST APIs, Firebase and third-party services.',
    ],
  },
  {
    id: 'freelance',
    company: 'Freelance',
    short: 'Freelance',
    role: 'Full Stack Developer | ML | AI',
    location: 'Remote',
    period: '2018 — Present',
    type: 'Freelance',
    current: true,
    summary:
      'Long-running independent practice across web, mobile, data and blockchain — plus architecture and scalability consulting for teams that needed a second opinion.',
    stack: ['PostgreSQL', 'MongoDB', 'MySQL', 'REST APIs', 'Blockchain', 'DeFi'],
    points: [
      'Delivered web and mobile solutions across multiple stacks.',
      'Designed APIs with PostgreSQL, MongoDB and MySQL.',
      'Built blockchain and DeFi solutions.',
      'Provided architecture and scalability consulting.',
    ],
  },
  {
    id: 'robinson',
    company: 'Col John C. Robinson American Center',
    short: 'Robinson Center',
    role: 'Mobile Application Developer (Android)',
    location: 'Addis Ababa, Ethiopia',
    period: 'Jun 2018 — Sep 2018',
    type: 'Program',
    summary:
      'Early Android work in Java, alongside robotics and 3D modelling programmes at the centre.',
    stack: ['Java', 'Android SDK', 'Android UI', 'Robotics'],
    points: [
      'Developed Android applications using Java with a clean UI and performance focus.',
      'Designed and implemented Android UI components.',
      'Participated in robotics and 3D modeling workshops.',
    ],
  },
];

/** Cross-role leadership practice, kept separate from any single employer. */
export const leadership = [
  'Lead and mentor frontend and backend developers while remaining hands-on with implementation.',
  'Conduct technical code reviews and establish standards for quality, security, maintainability and consistency.',
  'Break complex business requirements into technical designs, actionable tasks and delivery milestones.',
  'Support sprint planning, estimation, prioritisation and technical delivery.',
  'Identify technical risks, architectural issues, performance bottlenecks and production risks early.',
  'Lead production incident investigation, root-cause analysis, remediation and preventative improvements.',
  'Make technical decisions based on scalability, maintainability, security, performance and delivery requirements.',
];
